import { Component, type ReactNode } from 'react';

interface State {
  failed: boolean;
}

/**
 * The last line before a blank page. React unmounts the whole tree when a
 * render throws, and the most common thrower here is a lazy chunk that has
 * stopped existing after a deploy. Rather than an empty screen, say so and
 * offer the one thing that fixes it.
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    console.error('[app] render failed', error);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-semibold text-ink-900">Something went wrong</h1>
        <p className="mt-3 text-ink-500">
          The page could not be shown. This usually happens right after an update — reloading
          fixes it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-8 inline-flex rounded-lg bg-ink-800 px-6 py-3 font-medium text-white hover:bg-ink-900"
        >
          Reload
        </button>
      </div>
    );
  }
}
